import React, { useState, useEffect } from 'react';
import { Facebook, Instagram, Twitter, Linkedin } from 'lucide-react';

const SOCIAL = {
  facebook: process.env.REACT_APP_FACEBOOK_URL || 'https://facebook.com/getbenefitsavings',
  instagram: process.env.REACT_APP_INSTAGRAM_URL || 'https://instagram.com/getbenefitsavings',
  twitter: process.env.REACT_APP_TWITTER_URL || 'https://x.com/getbenefitsavings',
  linkedin: process.env.REACT_APP_LINKEDIN_URL || 'https://linkedin.com/company/getbenefitsavings',
};
import { API } from '../config';
import { Link } from 'react-router-dom';

export default function Navigation() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [articlesOpen, setArticlesOpen] = useState(false);

  const [categories, setCategories] = useState([{ id: 'all', name: 'All Articles', icon: '📚' }]);

  const iconMap = {'Bus': '🚌', 'CreditCard': '💳', 'Eye': '👁️', 'Users': '👨\u200d👩\u200d👧\u200d👦', 'GraduationCap': '🎓', 'Tag': '🏷️', 'Heart': '❤️', 'Shield': '🛡️', 'PiggyBank': '💰', 'TrendingUp': '📈', 'Calendar': '📅', 'Dumbbell': '🏋️'};

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        // Get categories that have articles
        const [catRes, activeRes] = await Promise.all([
          fetch(`${API}/categories`),
          fetch(`${API}/articles/categories/list`)
        ]);
        const catData = await catRes.json();
        const activeData = await activeRes.json();
        const activeSlugs = activeData.categories.map(c => c.name);
        const filtered = catData.categories
          .filter(c => activeSlugs.includes(c.slug))
          .map(c => ({ id: c.slug, name: c.name, icon: iconMap[c.icon] || '📄' }));
        setCategories([{ id: 'all', name: 'All Articles', icon: '📚' }, ...filtered]);
      } catch (e) {
        console.error('Failed to fetch categories:', e);
      }
    };
    fetchCategories();
  }, []);

  return (
    <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-200 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center h-16 gap-6">
          <Link to="/" className="flex items-center gap-3">
            <img src="/logo.png" alt="GetBenefitSavings" className="h-12 w-auto" />
            <span className="text-xl font-bold text-gray-900">GetBenefitSavings</span>
          </Link>
          <div className="hidden md:flex items-center gap-1.5 ml-2 z-10">
            <a href={SOCIAL.facebook} target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="text-gray-400 hover:text-blue-600 transition-colors p-1"><Facebook size={16} /></a>
            <a href={SOCIAL.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="text-gray-400 hover:text-blue-600 transition-colors p-1"><Instagram size={16} /></a>
            <a href={SOCIAL.twitter} target="_blank" rel="noopener noreferrer" aria-label="X" className="text-gray-400 hover:text-blue-600 transition-colors p-1"><Twitter size={16} /></a>
            <a href={SOCIAL.linkedin} target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" className="text-gray-400 hover:text-blue-600 transition-colors p-1"><Linkedin size={16} /></a>
          </div>

          <div className="hidden md:flex gap-6 items-center flex-1 justify-center -ml-28">
            <a href="/" className="text-sm font-medium text-gray-600 hover:text-blue-600 transition-colors">Home</a>

            {/* Articles with dropdown */}
            <div
              className="relative"
              onMouseEnter={() => setArticlesOpen(true)}
              onMouseLeave={() => setArticlesOpen(false)}
            >
              <a
                href="/articles"
                className="text-sm font-medium text-gray-600 hover:text-blue-600 transition-colors flex items-center gap-1"
              >
                Articles
                <svg className={`w-4 h-4 transition-transform ${articlesOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </a>

              {articlesOpen && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 pt-2">
                  <div className="bg-white rounded-xl shadow-xl border border-gray-200 py-2 w-52">
                    {categories.map(cat => (
                      <a
                        key={cat.id}
                        href={cat.id === 'all' ? '/articles' : `/articles?category=${cat.id}`}
                        className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors"
                      >
                        <span>{cat.icon}</span>
                        {cat.name}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <a href="/#calculator" onClick={(e) => { e.preventDefault(); if (window.location.pathname === "/") { document.getElementById("calculator")?.scrollIntoView({ behavior: "smooth" }); } else { window.location.href = "/#calculator"; }}} className="text-sm font-medium text-gray-600 hover:text-blue-600 transition-colors">Calculator</a>
            <Link to="/contact" className="text-sm font-medium text-gray-600 hover:text-blue-600 transition-colors">Contact</Link>
            <a href="/news" className="text-sm font-medium text-gray-600 hover:text-blue-600 transition-colors">News</a>
          </div>

          <Link to="/getstarted" className="hidden md:block bg-blue-600 text-white hover:bg-blue-700 px-6 py-2 rounded-lg font-semibold text-sm transition-all">
            Get Started
          </Link>

          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            {mobileMenuOpen ? (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            )}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div className="md:hidden border-t border-gray-200 bg-white">
          <div className="px-4 py-4 space-y-3">
            <a href="/" className="block px-4 py-3 rounded-lg text-gray-700 hover:bg-gray-100 hover:text-blue-600 font-medium transition-colors" onClick={() => setMobileMenuOpen(false)}>
              🏠 Home
            </a>
            <a href="/articles" className="block px-4 py-3 rounded-lg text-gray-700 hover:bg-gray-100 hover:text-blue-600 font-medium transition-colors" onClick={() => setMobileMenuOpen(false)}>
              📚 All Articles
            </a>
            {/* Mobile category links */}
            {categories.filter(c => c.id !== 'all').map(cat => (
              <a
                key={cat.id}
                href={`/articles?category=${cat.id}`}
                className="block px-4 py-3 pl-10 rounded-lg text-gray-600 hover:bg-gray-100 hover:text-blue-600 text-sm transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                {cat.icon} {cat.name}
              </a>
            ))}
            <a href="/#calculator" className="block px-4 py-3 rounded-lg text-gray-700 hover:bg-gray-100 hover:text-blue-600 font-medium transition-colors" onClick={() => { setMobileMenuOpen(false); if (window.location.pathname !== "/") { window.location.href = "/#calculator"; }}}>
              🧮 Calculator
            </a>
            <Link to="/contact" className="block px-4 py-3 rounded-lg text-gray-700 hover:bg-gray-100 hover:text-blue-600 font-medium transition-colors" onClick={() => setMobileMenuOpen(false)}>
              ✉️ Contact
            </Link>
            <a href="/news" className="block px-4 py-3 rounded-lg text-gray-700 hover:bg-gray-100 hover:text-blue-600 font-medium transition-colors" onClick={() => setMobileMenuOpen(false)}>
              📰 News
            </a>
            <Link to="/getstarted" className="block w-full text-center bg-blue-600 text-white hover:bg-blue-700 px-6 py-3 rounded-lg font-semibold transition-all" onClick={() => setMobileMenuOpen(false)}>
              Get Started
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
}
